import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiMail, FiPhone, FiGlobe, FiChevronLeft } from 'react-icons/fi';
import merchantService from '../../services/merchant.service';
import productService from '../../services/product.service';
import VerifiedBadge, { VerifiedCheck } from '../../components/merchant/VerifiedBadge';
import FollowStoreButton from '../../components/merchant/FollowStoreButton';
import ProductCard from '../../components/product/ProductCard';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const StoreDetailPage = () => {
  const { slug } = useParams();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);

  useEffect(() => {
    const fetchStore = async () => {
      setLoading(true);
      try {
        const { data } = await merchantService.getStore(slug);
        setStore(data.data);

        // Fetch store's products
        setProductsLoading(true);
        const prodRes = await productService.getAll({ merchant: data.data.userId, limit: 20 });
        setProducts(prodRes.data.data);
      } catch (error) {
        console.error('Error fetching store:', error);
      } finally {
        setLoading(false);
        setProductsLoading(false);
      }
    };
    fetchStore();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) return <LoadingSpinner />;

  if (!store) {
    return (
      <div className="section-padding text-center">
        <h3>Store not found</h3>
        <Link to="/stores" className="btn btn-ebw-primary mt-3">Browse Stores</Link>
      </div>
    );
  }

  return (
    <div>
      {/* Store Banner */}
      <div
        className="text-white py-5"
        style={{
          background: store.banner
            ? `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${store.banner}) center/cover`
            : store.branding?.primaryColor || '#B91C1C',
          minHeight: '200px',
        }}
      >
        <div className="container">
          <Link to="/stores" className="text-white text-decoration-none d-inline-flex align-items-center gap-1 mb-3 opacity-75">
            <FiChevronLeft /> All Stores
          </Link>
          <div className="d-flex align-items-center gap-3">
            {store.logo ? (
              <img
                src={store.logo}
                alt={store.storeName}
                className="rounded-circle border border-white border-2"
                style={{ width: '80px', height: '80px', objectFit: 'cover' }}
              />
            ) : (
              <div
                className="rounded-circle d-flex align-items-center justify-content-center border border-white border-2"
                style={{ width: '80px', height: '80px', background: 'rgba(255,255,255,0.2)', fontSize: '32px', fontWeight: 'bold' }}
              >
                {store.storeName?.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <h1 className="mb-1" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                {store.storeName}
                <VerifiedCheck verification={store.verification} size={18} />
              </h1>
              <VerifiedBadge verification={store.verification} size="sm" showBadges inline />
              <div className="d-flex align-items-center gap-2 mt-1">
                {store.description && <p className="mb-0 opacity-75">{store.description}</p>}
              </div>
              <div className="mt-2">
                <FollowStoreButton storeId={store._id} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="section-padding">
        <div className="container">
          <div className="row g-4">
            {/* Products */}
            <div className="col-lg-9">
              <h3 className="mb-4">Products ({products.length})</h3>
              {productsLoading ? (
                <LoadingSpinner />
              ) : products.length > 0 ? (
                <div className="row g-4">
                  {products.map((product) => (
                    <div key={product._id} className="col-sm-6 col-md-4">
                      <ProductCard product={product} />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted">This store has no products yet.</p>
              )}
            </div>

            {/* Store Info Sidebar */}
            <div className="col-lg-3">
              <div className="dashboard-card">
                <h5 className="mb-3">Store Info</h5>
                {store.contact?.email && (
                  <p className="small mb-2">
                    <FiMail className="me-2 text-muted" />
                    <a href={`mailto:${store.contact.email}`} className="text-decoration-none">
                      {store.contact.email}
                    </a>
                  </p>
                )}
                {store.contact?.phone && (
                  <p className="small mb-2">
                    <FiPhone className="me-2 text-muted" />
                    {store.contact.phone}
                  </p>
                )}
                {store.contact?.website && (
                  <p className="small mb-2">
                    <FiGlobe className="me-2 text-muted" />
                    <a href={store.contact.website} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                      Website
                    </a>
                  </p>
                )}
                {store.socialLinks && Object.entries(store.socialLinks).some(([, v]) => v) && (
                  <div className="mt-3 pt-3 border-top">
                    <h6 className="small text-muted mb-2">Follow Us</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {store.socialLinks.facebook && (
                        <a href={store.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="badge bg-light text-dark text-decoration-none">Facebook</a>
                      )}
                      {store.socialLinks.instagram && (
                        <a href={store.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="badge bg-light text-dark text-decoration-none">Instagram</a>
                      )}
                      {store.socialLinks.twitter && (
                        <a href={store.socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="badge bg-light text-dark text-decoration-none">Twitter</a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreDetailPage;
